/**
 * The edits the review screen offers, as pure functions on the document.
 *
 * Every edit is the user's word, so the result carries confidence 1 and no reason —
 * a merged or split boundary is no longer the detector's guess. Numbers always follow
 * page order; the number the user sees is never stale.
 */

import type { DetectedChapter, Gap, SourceDocument } from "./types";

const OWN = { confidence: 1, reason: null } as const;

export function renumber(chapters: DetectedChapter[]): DetectedChapter[] {
  return [...chapters]
    .sort((a, b) => a.startPage - b.startPage)
    .map((c, i) => ({ ...c, number: i + 1 }));
}

function replace(doc: SourceDocument, chapters: DetectedChapter[]): SourceDocument {
  return { ...doc, chapters: renumber(chapters) };
}

export function mergeWithNext(doc: SourceDocument, number: number): SourceDocument {
  const ordered = renumber(doc.chapters);
  const i = ordered.findIndex((c) => c.number === number);
  const here = ordered[i];
  const next = ordered[i + 1];
  if (!here || !next) return doc;
  // Included if either side was: the student wanted at least one of them in.
  const included = (here.included ?? true) || (next.included ?? true);
  const merged = { ...here, endPage: next.endPage, included, ingested: false, ...OWN };
  return replace(doc, [...ordered.slice(0, i), merged, ...ordered.slice(i + 2)]);
}

export function splitAt(doc: SourceDocument, number: number, page: number): SourceDocument {
  const target = doc.chapters.find((c) => c.number === number);
  if (!target || page <= target.startPage || page > target.endPage) return doc;
  const head = { ...target, endPage: page - 1, ingested: false, ...OWN };
  const tail = {
    ...target,
    title: `${target.title} (continued)`,
    startPage: page,
    ingested: false,
    ...OWN,
  };
  return replace(doc, [...doc.chapters.filter((c) => c.number !== number), head, tail]);
}

/** Skip for now, or include again: the row stays, with its evidence (spec 2026-09-19 §6.6). */
export function toggleIncluded(doc: SourceDocument, number: number): SourceDocument {
  return {
    ...doc,
    chapters: doc.chapters.map((c) =>
      c.number === number ? { ...c, included: !(c.included ?? true) } : c,
    ),
  };
}

export function removeChapter(doc: SourceDocument, number: number): SourceDocument {
  return replace(
    doc,
    doc.chapters.filter((c) => c.number !== number),
  );
}

export function addChapter(
  doc: SourceDocument,
  range: { title: string; startPage: number; endPage: number },
): SourceDocument {
  return replace(doc, [...doc.chapters, { number: 0, ...range, ...OWN }]);
}

function claimedPages(doc: SourceDocument): Set<number> {
  const claimed = new Set<number>();
  for (const c of doc.chapters) {
    for (let p = Math.max(1, c.startPage); p <= Math.min(doc.pageCount, c.endPage); p++) {
      claimed.add(p);
    }
  }
  return claimed;
}

/** Runs of consecutive pages in [from, to] that are not in `claimed`. */
function freeRuns(
  from: number,
  to: number,
  claimed: Set<number>,
): { startPage: number; endPage: number }[] {
  const runs: { startPage: number; endPage: number }[] = [];
  let start: number | null = null;
  for (let p = from; p <= to + 1; p++) {
    const free = p <= to && !claimed.has(p);
    if (free && start === null) start = p;
    if (!free && start !== null) {
      runs.push({ startPage: start, endPage: p - 1 });
      start = null;
    }
  }
  return runs;
}

/** The longest run of pages no chapter claims — where "Add chapter" puts a new one. */
export function largestUnassignedRun(
  doc: SourceDocument,
): { startPage: number; endPage: number } | null {
  const runs = freeRuns(1, doc.pageCount, claimedPages(doc));
  let best: { startPage: number; endPage: number } | null = null;
  for (const run of runs) {
    if (!best || run.endPage - run.startPage > best.endPage - best.startPage) best = run;
  }
  return best;
}

/** The detector's labelled gaps, clipped to pages the user's edits have not claimed. */
export function visibleGaps(doc: SourceDocument): Gap[] {
  const claimed = claimedPages(doc);
  const out: Gap[] = [];
  for (const gap of doc.gaps ?? []) {
    for (const run of freeRuns(gap.startPage, gap.endPage, claimed)) {
      out.push({ ...run, label: gap.label });
    }
  }
  return out;
}
